import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { dataCleanupReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have them
// const image1 = `${process.env.PUBLIC_URL}/assets/dataCleanupExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/dataCleanupExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/dataCleanupExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function DataCleanup() {
  const safeReviews = Array.isArray(dataCleanupReviews) ? dataCleanupReviews : [];
  const title = "Data Cleanup & Migration — Clean, Organize, and Prepare Your Data";
  const description =
    "Clean up messy spreadsheets, remove duplicates, standardize data, and prepare records for imports, reporting, and migration into new systems.";
  const canonical = "https://brainwavesolutions.net/services/data-cleanup";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Data Cleanup & Migration", url: canonical },
    ]),
    buildServiceSchema({
      name: "Data Cleanup & Migration",
      description,
      canonical,
      offerLabel: "$75–$1,500+",
      serviceType: "Data cleanup and migration",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "Spreadsheet showing cleaned customer data with standardized columns, removed duplicates, and normalized values.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Data migration worksheet with mapped source and destination fields for import into a new system.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Large spreadsheet showing deduplicated records, cleaned categories, and validated import-ready data.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Data cleanup and migration that turns messy exports into usable information
              </Typography>

              <p className="bws-gsl-lead">
                Bad data creates problems everywhere downstream. Duplicates pile up, categories don’t match, names and
                dates are formatted inconsistently, and imports fail because the source file is more chaotic than it
                looks at first glance.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses and individuals clean up messy datasets, standardize information, remove duplicates,
                and prepare files for reporting, imports, migrations, and day-to-day use. The goal is simple: clean
                data you can actually trust.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Duplicate removal</span>
                <span className="bws-gsl-badge">Standardization</span>
                <span className="bws-gsl-badge">Import preparation</span>
                <span className="bws-gsl-badge">Migration support</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help cleaning data
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common data problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Duplicate records and inconsistent entries</li>
                  <li>Mixed date, currency, and text formatting</li>
                  <li>Exports that are hard to import into another system</li>
                  <li>Messy categories, labels, and naming conventions</li>
                  <li>Data that needs to be cleaned before reporting or migration</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with a sample file or describe the format issues, duplicates, or migration goal you’re dealing with.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Clean up the mess</Typography>
              <Typography className="bws-gsl-stripText">
                Remove duplicates, fix inconsistencies, and turn scattered data into something usable.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Prepare it for the next step</Typography>
              <Typography className="bws-gsl-stripText">
                Get files ready for imports, dashboards, reporting, or migration into a new system.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Reduce downstream errors</Typography>
              <Typography className="bws-gsl-stripText">
                Clean source data means fewer reporting issues, fewer import failures, and more reliable workflows.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of cleaned, standardized, and import-ready data files.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Duplicate removal and record cleanup</li>
                    <li>Category mapping and standardization</li>
                    <li>Spreadsheet and CSV cleanup</li>
                    <li>Import-ready file preparation</li>
                    <li>Migration support for moving data into new systems</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Businesses moving data between systems</li>
                    <li>Teams with messy exports and inconsistent records</li>
                    <li>Reporting workflows that depend on clean source data</li>
                    <li>Anyone preparing spreadsheets for import, analysis, or migration</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help if my data is spread across multiple files?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. I can help combine, standardize, and clean data from multiple sources so it is easier to use and easier to trust.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you prepare data for importing into another system?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. I can help clean, map, and reformat your data so it is ready for import into a new tool or workflow.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the issue and your timeframe requirements.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help cleaning or migrating data?</Typography>
              <p className="bws-muted">
                Send a quick description of the data issue and what you need the cleaned file to do next.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
